import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';
import styled from 'styled-components';
import {
  GRANTED,
  DENIED,
  getStoredConsent,
  setConsent,
  applyStoredConsent,
} from '../services/analyticsConsent';


/**
 * The Google Analytics consent banner (DWEB-15).
 *
 * It ships WITH the updated privacy policy (DWEB-21) and not before: that
 * policy tells visitors analytics are consent-gated and that they can decline,
 * so until this exists the policy describes a control that does not.
 *
 * It is about GOOGLE ANALYTICS ONLY. The DynaMite chat (a hosted service since
 * v6.4.8, DWEB-121) carries its own notice before the first message
 * (DynaMiteWidget.js); until then the local model had its own separate
 * consent dialog in Chatbot.js and the two are deliberately not merged —
 * implying the model phones home would contradict the whole point of running
 * it in the browser.
 *
 * Decline is exactly as easy as accept: same size, same weight, same place,
 * one click each. A banner where refusing costs more effort than agreeing is
 * not consent.
 */
const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  /* 1001 is load-bearing, and the window is narrow. The stack here:
       Footer 1000 · chat launcher 1000 · chat panel 1000-1002 ·
       the model's consent overlay 1003 (Chatbot.js:451 — a custom fixed
       overlay, NOT an antd Modal).
     This bar must sit ABOVE the footer, or the footer swallows clicks on
     Decline; and BELOW 1003, or it covers the model consent dialog.

     Both failure modes were real and both were caught by the suite. At 2000
     this bar intercepted pointer events on the model dialog's
     "Download & Continue" button — a first-time visitor, who by definition
     sees both, could read that dialog and not act on it (6 chat scenarios
     went red). At 900 the footer intercepted Decline instead (4 consent
     scenarios went red). consent.spec.js pins the surviving behaviour by
     clicking through, not by asserting the number. */
  z-index: 1001;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.85rem 1.25rem;
  /* Extra right padding keeps the text and buttons out from under the fixed
     chat launcher, which lives in the bottom-right corner. */
  padding: 1rem 6.5rem 1rem 1.25rem;
  background: rgba(15, 16, 34, 0.97);
  border-top: 1px solid rgba(245, 158, 11, 0.45);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.45);
`;

const Actions = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
`;

/** A quiet control to reopen the choice, for the footer. */
export function ConsentSettingsLink({ onReopen }) {
  return (
    <Button type="link" size="small" onClick={onReopen} style={{ padding: 0, height: 'auto' }}>
      Analytics preferences
    </Button>
  );
}

/** One style for both choices — see the note where they are rendered. */
const choiceStyle = { minWidth: '6.5rem' };

function ConsentBanner() {
  // `null` = not yet answered, and therefore denied until it is.
  const [choice, setChoice] = useState(() => getStoredConsent());
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    // Replay a stored grant on load. A stored denial needs no action: the
    // default is denied and gtag.js was never injected.
    applyStoredConsent();
  }, []);

  const decide = useCallback((value) => {
    setChoice(setConsent(value));
    setReopened(false);
  }, []);

  const reopen = useCallback(() => setReopened(true), []);

  // Exposed so the footer (or anything else) can reopen the choice without
  // this component owning a global. The requirement is that the decision is
  // revisitable, not that it is buried.
  useEffect(() => {
    window.__openAnalyticsPreferences = reopen;
    return () => {
      delete window.__openAnalyticsPreferences;
    };
  }, [reopen]);

  if (choice !== null && !reopened) return null;

  return (
    <Bar role="region" aria-label="Analytics cookie choice" data-testid="consent-banner">
      {/* DWEB-213: a plain span, not antd Typography's Text (see About.js). The bar has no antd
          ancestor to inherit antd's font stack from, so the span states it with Text's size,
          unitless line-height and word-break. */}
      <span data-typo="" style={{ color: '#e5e7eb', maxWidth: '46rem', textShadow: '0 1px 3px rgba(0,0,0,0.8)', fontSize: '14px', lineHeight: 1.5714285714285714, wordBreak: 'break-word', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'" }}>
        We use Google Analytics to count visits and see which pages are read. It only runs if you
        say yes. The DynaMite chat is a separate hosted service and is not part of it.
      </span>
      <Actions>
        {/*
          IDENTICAL controls, deliberately (DWEB-15 F2). Accept was the antd
          `primary` variant, so it carried a filled blue weight that Decline did
          not. Same size and same click cost, but not the same pull — and a site
          that sells privacy architecture cannot then nudge you toward switching
          analytics on. The only thing at stake is Google Analytics; the
          credibility is worth more.

          Same variant, same colour, same weight, same width. The choice is
          presented, not steered. consent.spec.js asserts the equivalence rather
          than trusting this comment.
        */}
        <Button onClick={() => decide(DENIED)} data-testid="consent-decline" style={choiceStyle}>
          Decline
        </Button>
        <Button onClick={() => decide(GRANTED)} data-testid="consent-accept" style={choiceStyle}>
          Accept
        </Button>
      </Actions>
    </Bar>
  );
}

export default ConsentBanner;
